import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { categoryService, CreateCategoryRequest } from '@/features/admin/services/categoryService';
import { sectionService, SectionDto } from '@/features/admin/services/sectionService';
import { uploadService } from '@/features/admin/services/uploadService';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ArrowRight } from 'lucide-react';

const createCategorySchema = z.object({
  nameAr: z.string().min(1, 'اسم الفئة بالعربي مطلوب').max(200, 'الاسم طويل جداً'),
  name: z.string().max(200, 'الاسم طويل جداً').optional(),
  descriptionAr: z.string().optional(),
  description: z.string().optional(),
  sectionId: z.string().optional(),
  displayOrder: z.number().min(0).default(0),
  enableForNewUsers: z.boolean().default(false),
});

type CreateCategoryFormData = z.infer<typeof createCategorySchema>;

export const CreateCategoryPage = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState<SectionDto[]>([]);
  const [loadingSections, setLoadingSections] = useState(true);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateCategoryFormData>({
    resolver: zodResolver(createCategorySchema),
    defaultValues: {
      displayOrder: 0,
      enableForNewUsers: false,
    },
  });

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      setLoadingSections(true);
      const data = await sectionService.getAllSections({ pageSize: 100 });
      setSections(data.sections);
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    } finally {
      setLoadingSections(false);
    }
  };

  const onSubmit = async (data: CreateCategoryFormData) => {
    try {
      const request: CreateCategoryRequest = {
        name: data.name || data.nameAr, // استخدم العربي إذا لم يوجد إنجليزي
        nameAr: data.nameAr,
        description: data.description || undefined,
        descriptionAr: data.descriptionAr || undefined,
        sectionId: data.sectionId || undefined,
        imageUrl: imageUrl || undefined,
        displayOrder: data.displayOrder,
        enableForNewUsers: data.enableForNewUsers,
      };

      await categoryService.createCategory(request);
      toast.success('تم إنشاء الفئة بنجاح');
      navigate('/admin/categories');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل إنشاء الفئة');
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-accent-dark mb-2">إضافة فئة جديدة</h1>
        <p className="text-accent-dark/70">أضف فئة جديدة لتنظيم الأسئلة</p>
      </div>

      <Card className="border-2 border-accent-greenLight/20">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-accent-dark">معلومات الفئة</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Section Selection */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                القسم
              </label>
              <select
                {...register('sectionId')}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
                disabled={loadingSections}
              >
                <option value="">-- اختر القسم (اختياري) --</option>
                {sections.filter(s => s.isEnabled).map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">يمكنك تحديد القسم الذي تنتمي إليه هذه الفئة</p>
            </div>

            {/* اسم الفئة بالعربي - مطلوب */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                اسم الفئة (عربي) *
              </label>
              <Input
                {...register('nameAr')}
                placeholder="مثال: العلوم"
                error={errors.nameAr?.message}
                className="text-lg"
              />
            </div>

            {/* اسم الفئة بالإنجليزي - اختياري */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                اسم الفئة (إنجليزي)
                <span className="text-xs text-gray-400 mr-2">(اختياري)</span>
              </label>
              <Input
                {...register('name')}
                placeholder="مثال: Science"
                error={errors.name?.message}
                className="text-lg"
              />
            </div>

            {/* الوصف بالعربي */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                الوصف (عربي)
              </label>
              <textarea
                {...register('descriptionAr')}
                placeholder="وصف الفئة بالعربية..."
                rows={3}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              />
            </div>

            {/* الوصف بالإنجليزي - اختياري */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                الوصف (إنجليزي)
                <span className="text-xs text-gray-400 mr-2">(اختياري)</span>
              </label>
              <textarea
                {...register('description')}
                placeholder="Category description..."
                rows={3}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              />
            </div>

            {/* أيقونة/لوجو الفئة */}
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                أيقونة الفئة
              </label>
              <p className="text-xs text-gray-500 mb-3">
                صورة مربعة بحجم 1024×1024 بكسل (ستظهر كأيقونة/لوجو)
              </p>
              <ImageUpload
                value={imageUrl || undefined}
                onChange={(url) => setImageUrl(url)}
                onUpload={uploadService.uploadCategoryImage}
                placeholder="ارفع أيقونة الفئة"
                shape="square"
                sizeHint="1024×1024 بكسل"
              />
            </div>

            {/* تفعيل للمستخدمين الجدد */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enableForNewUsers"
                {...register('enableForNewUsers')}
                className="w-5 h-5 rounded border-2 border-accent-greenLight/50 text-secondary focus:ring-secondary"
              />
              <label htmlFor="enableForNewUsers" className="text-sm font-medium text-accent-dark">
                تفعيل للمستخدمين الجدد (تظهر الفئة ملونة في التجربة المجانية حتى لو كان القسم غير مجاني)
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                ترتيب العرض
              </label>
              <Input
                type="number"
                {...register('displayOrder', { valueAsNumber: true })}
                placeholder="0"
                error={errors.displayOrder?.message}
                className="text-lg"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/categories')}
                className="flex-1"
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-secondary hover:bg-secondary-dark text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    إنشاء الفئة
                    <ArrowRight className="w-4 h-4 mr-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
